import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export type PillarKey = 'B' | 'H' | 'S';
type EdgeKey = 'BH' | 'BS' | 'HS';

interface BHSModelDiagramProps {
  title?: string;
  caption?: string;
  eyebrow?: string;
  showHeader?: boolean;
  showCaption?: boolean;
  showOuterRing?: boolean;
  className?: string;
  /**
   * Horizontal padding of the inner wrapper. Pass '' for full-bleed (the home grid
   * column needs every pixel - see the effective-px note below).
   *
   * This exists as its own prop because overriding via `className` does NOT work:
   * Tailwind emits `.px-0` before `.px-4`, so a caller passing `className="px-0"`
   * silently loses to the default `px-4`.
   */
  innerPaddingClassName?: string;
  containerClassName?: string;
  background?: 'cream' | 'transparent';
  subtleFloat?: boolean;
}

const BUSINESS_COLOR = '#1A365D';
const HUMAN_COLOR = '#8B1538';
const SPIRIT_COLOR = '#C9A961';
const INK = '#1A2B3C';

const SERIF: CSSProperties = { fontFamily: '"Lora", serif' };

const PILLARS: Record<PillarKey, { x: number; y: number; color: string; label: string; sub: string; labelY: number; subY: number }> = {
  B: { x: 360, y: 110, color: BUSINESS_COLOR, label: 'Business', sub: 'STRUKTUREN & ZIELE', labelY: 200, subY: 224 },
  H: { x: 130, y: 460, color: HUMAN_COLOR, label: 'Human', sub: 'MENSCH & BEZIEHUNG', labelY: 552, subY: 576 },
  S: { x: 590, y: 460, color: SPIRIT_COLOR, label: 'Spirit', sub: 'BEWUSSTSEIN', labelY: 552, subY: 576 },
};

// Edge labels are split across two lines at the ampersand. At fontSize 14 a single
// line ("FÜHRUNG & KOMMUNIKATION" ~218 user units) would overrun the 231-unit corridor
// between the outer ring and the centre circle. Every label is an "X & Y" conjunction,
// so the ampersand is also the natural typographic break.
const EDGES: Record<EdgeKey, { x1: number; y1: number; x2: number; y2: number; lx: number; ly: number; line1: string; line2: string; ends: [PillarKey, PillarKey] }> = {
  BH: { x1: 326.7, y1: 162.3, x2: 163.3, y2: 407.7, lx: 195, ly: 270, line1: 'FÜHRUNG &', line2: 'KOMMUNIKATION', ends: ['B', 'H'] },
  BS: { x1: 394.1, y1: 161.8, x2: 555.9, y2: 408.2, lx: 525, ly: 270, line1: 'VISION &', line2: 'UMSETZUNG', ends: ['B', 'S'] },
  HS: { x1: 192, y1: 460, x2: 528, y2: 460, lx: 360, ly: 494, line1: 'BEWUSSTSEIN &', line2: 'ENTWICKLUNG', ends: ['H', 'S'] },
};

const PILLAR_EDGES: Record<PillarKey, EdgeKey[]> = {
  B: ['BH', 'BS'],
  H: ['BH', 'HS'],
  S: ['BS', 'HS'],
};

export default function BHSModelDiagram({
  title = 'Das BHS-Modell',
  caption = 'Die Integration von Business, Mensch und Bewusstsein für nachhaltige Transformation.',
  eyebrow,
  showHeader = true,
  showCaption = true,
  showOuterRing = true,
  className = '',
  innerPaddingClassName = 'px-4',
  containerClassName = '',
  background = 'cream',
  subtleFloat = true,
}: BHSModelDiagramProps) {
  const reactId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const scopeClass = `bhs-svg-${reactId}`;

  const svgRef = useRef<SVGSVGElement>(null);
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState<PillarKey | null>(null);

  useEffect(() => {
    const node = svgRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const setHoveredPillar = (p: PillarKey | null) => setHovered(p);

  const isEdgeActive = (edge: EdgeKey) => hovered !== null && PILLAR_EDGES[hovered].includes(edge);
  const isEdgeDimmed = (edge: EdgeKey) => hovered !== null && !PILLAR_EDGES[hovered].includes(edge);
  const isPillarDimmed = (p: PillarKey) => hovered !== null && hovered !== p;

  const edgeStroke = (edge: EdgeKey): CSSProperties => {
    const active = isEdgeActive(edge);
    const dimmed = isEdgeDimmed(edge);
    const accent = hovered ? PILLARS[hovered].color : INK;
    return {
      stroke: active ? accent : INK,
      strokeOpacity: active ? 0.9 : dimmed ? 0.1 : 0.32,
      strokeWidth: active ? 1.4 : 1,
      strokeDasharray: active ? '4 4' : '3 6',
      transition: 'stroke 250ms ease, stroke-opacity 250ms ease, stroke-width 250ms ease, stroke-dasharray 250ms ease',
    };
  };

  const labelOpacity = (edge: EdgeKey) => {
    if (hovered === null) return 0.78;
    return isEdgeActive(edge) ? 1 : 0.32;
  };

  const bgClass = background === 'cream' ? 'bg-[#FAF6EE]' : 'bg-transparent';

  const styles = `
    .${scopeClass} [data-anim] { opacity: 0; }
    .${scopeClass} [data-pillar-anim] { opacity: 0; }
    .${scopeClass} [data-center-anim] {
      opacity: 0;
      transform-box: fill-box;
      transform-origin: center;
    }

    .${scopeClass}.is-in [data-anim="ring"] {
      animation: bhs-fade 700ms cubic-bezier(.2,.7,.2,1) 80ms forwards;
    }
    .${scopeClass}.is-in [data-anim="line"] {
      animation: bhs-fade 700ms cubic-bezier(.2,.7,.2,1) 500ms forwards;
    }
    .${scopeClass}.is-in [data-anim="label"] {
      animation: bhs-rise 600ms cubic-bezier(.2,.7,.2,1) 1100ms forwards;
    }
    .${scopeClass}.is-in [data-anim="pillar-label"] {
      animation: bhs-rise 600ms cubic-bezier(.2,.7,.2,1) var(--bhs-delay, 700ms) forwards;
    }
    .${scopeClass}.is-in [data-pillar-anim="B"] { animation: bhs-pop 600ms cubic-bezier(.2,.7,.2,1) 600ms forwards; }
    .${scopeClass}.is-in [data-pillar-anim="H"] { animation: bhs-pop 600ms cubic-bezier(.2,.7,.2,1) 750ms forwards; }
    .${scopeClass}.is-in [data-pillar-anim="S"] { animation: bhs-pop 600ms cubic-bezier(.2,.7,.2,1) 900ms forwards; }
    .${scopeClass}.is-in [data-center-anim] { animation: bhs-pop 700ms cubic-bezier(.2,.7,.2,1) 1300ms forwards; }

    .${scopeClass} .bhs-glow { transform-box: fill-box; transform-origin: center; opacity: 0.8; }
    .${scopeClass}.is-in .bhs-glow {
      animation: bhs-breathe 5.2s ease-in-out 1500ms infinite;
    }

    .${scopeClass} .bhs-pillar {
      transform-box: fill-box;
      transform-origin: center;
      transition: transform 280ms cubic-bezier(.2,.7,.2,1), opacity 250ms ease, filter 250ms ease;
      cursor: pointer;
      outline: none;
    }
    .${scopeClass} .bhs-pillar:hover { transform: scale(1.06); }
    .${scopeClass} .bhs-pillar:focus { outline: none; }
    .${scopeClass} .bhs-pillar:focus-visible { outline: 2px solid ${SPIRIT_COLOR}; outline-offset: 4px; border-radius: 50%; }
    .${scopeClass} .bhs-pillar.is-dimmed { opacity: 0.5; }

    .${scopeClass} .bhs-center {
      transform-box: fill-box;
      transform-origin: center;
      transition: transform 280ms cubic-bezier(.2,.7,.2,1);
    }
    .${scopeClass}.has-hover .bhs-center { transform: scale(1.04); }

    ${subtleFloat ? `
      .${scopeClass}.is-in .bhs-float-B { animation: bhs-float-up 8s ease-in-out 2s infinite; transform-box: fill-box; transform-origin: center; }
      .${scopeClass}.is-in .bhs-float-H { animation: bhs-float-down 9s ease-in-out 2.4s infinite; transform-box: fill-box; transform-origin: center; }
      .${scopeClass}.is-in .bhs-float-S { animation: bhs-float-up 9.5s ease-in-out 2.7s infinite; transform-box: fill-box; transform-origin: center; }
    ` : ''}

    @keyframes bhs-fade {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes bhs-rise {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes bhs-pop {
      0% { opacity: 0; transform: scale(0.55); }
      60% { opacity: 1; transform: scale(1.04); }
      100% { opacity: 1; transform: scale(1); }
    }
    @keyframes bhs-breathe {
      0%, 100% { opacity: 0.78; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.05); }
    }
    @keyframes bhs-float-up {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-2.5px); }
    }
    @keyframes bhs-float-down {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(2.5px); }
    }

    @media (prefers-reduced-motion: reduce) {
      .${scopeClass} [data-anim],
      .${scopeClass} [data-pillar-anim],
      .${scopeClass} [data-center-anim] { opacity: 1 !important; }
      .${scopeClass} *,
      .${scopeClass}.is-in *,
      .${scopeClass}.is-in .bhs-glow,
      .${scopeClass}.is-in .bhs-float-B,
      .${scopeClass}.is-in .bhs-float-H,
      .${scopeClass}.is-in .bhs-float-S {
        animation: none !important;
        transition: opacity 200ms ease, fill-opacity 200ms ease !important;
      }
    }
  `;

  const handlePillarEnter = (p: PillarKey) => () => setHoveredPillar(p);
  const handlePillarLeave = () => setHoveredPillar(null);

  return (
    <div className={`relative isolate w-full ${bgClass} ${containerClassName}`}>
      {background === 'cream' && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, #1A2B3C 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
      )}

      <div className={`relative mx-auto w-full max-w-3xl ${innerPaddingClassName} ${className}`}>
        {showHeader && (
          <div className="mb-6 text-center lg:mb-10">
            {eyebrow && (
              <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-[0.32em] text-[#8B1538] lg:text-[13px]">
                {eyebrow}
              </span>
            )}
            <h2
              className="font-serif text-3xl font-bold leading-tight text-[#1A365D] md:text-4xl lg:text-[2.75rem]"
              style={SERIF}
            >
              {title}
            </h2>
            <div className="mx-auto mt-4 h-px w-12 bg-[#C9A961]" />
          </div>
        )}

        {/* Diagram */}
        <div className="relative mx-auto w-full">
          <style>{styles}</style>
          <svg
            ref={svgRef}
            viewBox="0 0 720 640"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="BHS-Modell - Business, Human, Spirit Integration"
            className={`block h-auto w-full ${scopeClass} ${inView ? 'is-in' : ''} ${hovered ? 'has-hover' : ''}`}
          >
            <defs>
              <radialGradient id={`bhs-glow-${reactId}`} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={SPIRIT_COLOR} stopOpacity="0.28" />
                <stop offset="55%" stopColor={SPIRIT_COLOR} stopOpacity="0.08" />
                <stop offset="100%" stopColor={SPIRIT_COLOR} stopOpacity="0" />
              </radialGradient>
              <filter id={`bhs-soft-${reactId}`} x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
                <feOffset dx="0" dy="2" result="offsetblur" />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="0.18" />
                </feComponentTransfer>
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id={`bhs-pillar-${reactId}`} x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
                <feOffset dx="0" dy="6" result="offsetblur" />
                <feComponentTransfer>
                  <feFuncA type="linear" slope="0.22" />
                </feComponentTransfer>
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {showOuterRing && (
              <g aria-hidden="true" data-anim="ring">
                <circle cx="360" cy="320" r="290" fill="none" stroke={INK} strokeOpacity="0.07" strokeWidth="1" />
                <circle cx="360" cy="320" r="240" fill="none" stroke={INK} strokeOpacity="0.04" strokeWidth="1" />
              </g>
            )}

            <circle
              className="bhs-glow"
              cx="360" cy="320" r="150"
              fill={`url(#bhs-glow-${reactId})`}
            />

            <g data-anim="line" fill="none" strokeLinecap="round">
              <line x1={EDGES.BH.x1} y1={EDGES.BH.y1} x2={EDGES.BH.x2} y2={EDGES.BH.y2} style={edgeStroke('BH')} />
              <line x1={EDGES.BS.x1} y1={EDGES.BS.y1} x2={EDGES.BS.x2} y2={EDGES.BS.y2} style={edgeStroke('BS')} />
              <line x1={EDGES.HS.x1} y1={EDGES.HS.y1} x2={EDGES.HS.x2} y2={EDGES.HS.y2} style={edgeStroke('HS')} />
            </g>

            <g data-anim="label">
              {(Object.keys(EDGES) as EdgeKey[]).map((key) => (
                <text
                  key={key}
                  x={EDGES[key].lx}
                  y={EDGES[key].ly}
                  textAnchor="middle"
                  fontFamily="Source Sans 3, system-ui, sans-serif"
                  fontSize="14"
                  fontWeight="600"
                  letterSpacing="1.2"
                  fill={INK}
                  fillOpacity={labelOpacity(key)}
                  style={{ transition: 'fill-opacity 250ms ease' }}
                >
                  <tspan x={EDGES[key].lx} dy="-8">{EDGES[key].line1}</tspan>
                  <tspan x={EDGES[key].lx} dy="17">{EDGES[key].line2}</tspan>
                </text>
              ))}
            </g>

            {(Object.keys(PILLARS) as PillarKey[]).map((p) => {
              const pil = PILLARS[p];
              return (
                <g
                  key={`label-${p}`}
                  data-anim="pillar-label"
                  style={{ '--bhs-delay': p === 'B' ? '900ms' : p === 'H' ? '1050ms' : '1200ms' } as CSSProperties}
                >
                  <text
                    x={pil.x} y={pil.labelY} textAnchor="middle"
                    fontSize="24" fontWeight="700" fill={INK}
                    style={SERIF}
                    fillOpacity={isPillarDimmed(p) ? 0.5 : 1}
                  >
                    {pil.label}
                  </text>
                  <text
                    x={pil.x} y={pil.subY} textAnchor="middle"
                    fontFamily="Source Sans 3, system-ui, sans-serif"
                    fontSize="13.5" fontWeight="500" letterSpacing="1.4"
                    fill={INK}
                    fillOpacity={isPillarDimmed(p) ? 0.32 : 0.6}
                  >
                    {pil.sub}
                  </text>
                </g>
              );
            })}

            {(Object.keys(PILLARS) as PillarKey[]).map((p) => {
              const pil = PILLARS[p];
              return (
                <g key={`node-${p}`} transform={`translate(${pil.x},${pil.y})`}>
                  <g className={`bhs-float-${p}`}>
                    <g
                      data-pillar-anim={p}
                      className={`bhs-pillar ${isPillarDimmed(p) ? 'is-dimmed' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-label={`${pil.label} - ${pil.sub.toLowerCase()}`}
                      onMouseEnter={handlePillarEnter(p)}
                      onMouseLeave={handlePillarLeave}
                      onFocus={handlePillarEnter(p)}
                      onBlur={handlePillarLeave}
                      onTouchStart={handlePillarEnter(p)}
                    >
                      <g filter={`url(#bhs-pillar-${reactId})`}>
                        <circle cx="0" cy="0" r="58" fill={pil.color} />
                        <circle cx="0" cy="0" r="58" fill="none" stroke="#FFFFFF" strokeOpacity={p === 'S' ? 0.18 : p === 'H' ? 0.1 : 0.08} strokeWidth="1" />
                      </g>
                      <text
                        x="0" y="14" textAnchor="middle"
                        fontSize="48" fontWeight="700" fill="#FFFFFF" style={SERIF}
                      >
                        {p}
                      </text>
                    </g>
                  </g>
                </g>
              );
            })}

            <g transform="translate(360,320)">
              {/* Animated/scaling group: only the circles. Text lives in a sibling
                  group below so it stays on the regular compositing layer and renders
                  crisp - putting text inside a transform-animated group causes
                  permanent sub-pixel rasterization blur on every browser. */}
              <g data-center-anim>
                <g className="bhs-center">
                  <g filter={`url(#bhs-soft-${reactId})`}>
                    <circle cx="0" cy="0" r="74" fill="#FFFFFF" />
                    <circle cx="0" cy="0" r="74" fill="none" stroke={SPIRIT_COLOR} strokeOpacity="0.55" strokeWidth="1.25" />
                    <circle cx="0" cy="0" r="68" fill="none" stroke={SPIRIT_COLOR} strokeOpacity="0.18" strokeWidth="1" />
                  </g>
                </g>
              </g>
              <text
                x="0" y="4" textAnchor="middle"
                fontSize="32" fontWeight="700" fill={BUSINESS_COLOR} style={SERIF}
              >
                BHS
              </text>
              <text
                x="0" y="26" textAnchor="middle"
                fontFamily="Source Sans 3, system-ui, sans-serif"
                fontSize="13.5" fontWeight="600" letterSpacing="1.8"
                fill="#8B1538"
              >
                WIRKSAMKEIT
              </text>
            </g>
          </svg>
        </div>

        {showCaption && (
          <p className="mx-auto mt-6 max-w-xl text-center text-sm leading-relaxed text-[#1A2B3C]/70 lg:mt-10 lg:text-base">
            {caption}
          </p>
        )}
      </div>
    </div>
  );
}
